import axios from "../../../../services/axios";
import { useReducer, useEffect } from "react";
import { DbUser } from "../../../../interfaces";
import { Role } from "../../../../enums";
import { AddSingleUserProps } from "../AddDetails/AddSingleUser";
import { logger } from "../../../../services/logger";

export type SchoolCreationState = {
  form: {
    name: string;
    district: string;
  };
  data: {
    districts: {
      value: string[];
      succeeded: boolean;
    };
  };
  currentUser: DbUser;
};

export type UpdateFormAction = {
  type: "updateForm";
  update: Partial<SchoolCreationState["form"]>;
};
export type ReplaceStateAction = {
  type: "replaceState";
  state: SchoolCreationState;
};

export type DataSuccessAction<T extends keyof SchoolCreationState["data"]> = {
  type: "dataSuccess";
  key: T;
  value: SchoolCreationState["data"][T]["value"];
};

export type Action =
  | UpdateFormAction
  | ReplaceStateAction
  | DataSuccessAction<keyof SchoolCreationState["data"]>;

export const dataSuccess = <T extends keyof SchoolCreationState["data"]>({
  key,
  value,
}: Omit<DataSuccessAction<T>, "type">): DataSuccessAction<T> => ({
  type: "dataSuccess",
  key,
  value,
});

export const initSchoolCreationState = (
  currentUser: DbUser,
  data?: SchoolCreationState["data"]
): SchoolCreationState => {
  const state: SchoolCreationState = {
    form: {
      name: "",
      district: "",
    },
    data: data ?? {
      districts: {
        value: [],
        succeeded: false,
      },
    },
    currentUser,
  };

  return state;
};

function reducer(state: SchoolCreationState, action: Action): SchoolCreationState {
  switch (action.type) {
    case "replaceState":
      return action.state;
    case "updateForm": {
      return {
        ...state,
        form: { ...state.form, ...action.update },
      };
    }
    case "dataSuccess": {
      return {
        ...state,
        data: {
          ...state.data,
          [action.key]: {
            value: action.value,
            succeeded: true,
          },
        },
      };
    }
  }
}

export function getError(state: SchoolCreationState): string | undefined {
  if (state.form.name.trim() === "") {
    return "School name is required.";
  }
}

export function useSchoolCreationState(props: AddSingleUserProps) {
  const [state, dispatch] = useReducer(
    reducer,
    initSchoolCreationState(props.currentUser)
  );

  useEffect(() => {
    if (!props.open) return;
    if (props.currentUser.role !== Role.super) return;
    const getDistricts = async () => {
      try {
        const res = await axios.get<string[]>("/districts");
        dispatch(dataSuccess({ key: "districts", value: res.data }));
      } catch (e) {
        logger.error(e);
      }
    };
    getDistricts();
  }, [props.currentUser.role, props.open]);

  return [state, dispatch] as const;
}
